import type { CSSProperties } from "react";
import { PhoneInput, type CountryIso2 } from "react-international-phone";
import "react-international-phone/style.css";

import {
  DEFAULT_PHONE_COUNTRY,
  PREFERRED_PHONE_COUNTRIES,
  buildE164PhoneValue,
  extractNationalNumber,
  sanitizeMobileNumber,
  toCountryIso2,
  toPhoneCountryCode,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";
import { cn } from "@project/lib/utils";

type MobileNumberFieldProps = {
  value: string;
  onChange: (nationalNumber: string) => void;
  country?: string;
  onCountryChange?: (country: PhoneCountryCode) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  inputClassName?: string;
  required?: boolean;
};

export function MobileNumberField({
  value,
  onChange,
  country = DEFAULT_PHONE_COUNTRY,
  onCountryChange,
  placeholder,
  disabled,
  id,
  className,
  inputClassName,
  required,
}: MobileNumberFieldProps) {
  const iso2 = toCountryIso2(country);

  return (
    <PhoneInput
      className={cn("jivandhara-phone-input w-full", className)}
      defaultCountry={iso2}
      value={buildE164PhoneValue(value, country)}
      preferredCountries={PREFERRED_PHONE_COUNTRIES}
      forceDialCode
      disabled={disabled}
      placeholder={placeholder ?? "Mobile number"}
      inputClassName={cn("jivandhara-phone-input__field", inputClassName)}
      countrySelectorStyleProps={{
        buttonClassName: "jivandhara-phone-input__country-btn",
      }}
      inputProps={{
        id,
        required,
        inputMode: "tel",
        autoComplete: "tel",
      }}
      onChange={(phone, meta) => {
        const nextCountry = toPhoneCountryCode(meta.country.iso2);
        const national = sanitizeMobileNumber(
          extractNationalNumber(phone, meta.country.dialCode),
          nextCountry
        );
        onChange(national);
        if (nextCountry !== toPhoneCountryCode(country)) {
          onCountryChange?.(nextCountry);
        }
      }}
      style={
        {
          "--react-international-phone-height": "2.25rem",
          "--react-international-phone-font-size": "0.875rem",
          "--react-international-phone-border-radius":
            "calc(var(--radius) - 2px)",
          "--react-international-phone-border-color": "var(--input)",
          "--react-international-phone-background-color": "transparent",
          "--react-international-phone-text-color": "var(--foreground)",
          "--react-international-phone-disabled-background-color":
            "var(--muted)",
          "--react-international-phone-disabled-text-color":
            "var(--muted-foreground)",
          "--react-international-phone-country-selector-background-color":
            "transparent",
          "--react-international-phone-country-selector-background-color-hover":
            "var(--muted)",
          "--react-international-phone-country-selector-border-color":
            "var(--input)",
          "--react-international-phone-dropdown-item-background-color":
            "var(--card)",
          "--react-international-phone-dropdown-item-text-color":
            "var(--foreground)",
          "--react-international-phone-selected-dropdown-item-background-color":
            "var(--muted)",
          "--react-international-phone-selected-dropdown-item-text-color":
            "var(--foreground)",
          "--react-international-phone-dropdown-shadow":
            "0 1px 2px 0 rgb(0 0 0 / 0.05)",
          "--react-international-phone-flag-width": "1.25rem",
          "--react-international-phone-flag-height": "1.25rem",
        } as CSSProperties
      }
    />
  );
}

export type { CountryIso2 };
